<template>
  <v-menu
    :model-value="menuOpen"
    :close-on-content-click="false"
    transition="scale-transition"
    max-height="600"
    location="bottom"
    offset="6">
    <template #activator="{ props }">
      <v-badge
        v-model="hasUnreadNotifications"
        color="accent"
        offset-x="8"
        offset-y="8"
        dot>
        <v-btn
          v-show="filteredNotifications.length"
          v-bind="props"
          class="rounded-circle"
          size="small"
          icon="mdi-bell-outline"
          @click="menuOpen = true" />
      </v-badge>
    </template>

    <v-list width="460" lines="three" density="compact" rounded="lg">
      <v-list-item class="notification-header">
        <v-list-subheader class="text-h6">
          Notifications
          <v-tooltip location="right">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                variant="text"
                class="rounded-circle"
                size="small"
                icon
                @click="removeAll">
                <v-icon size="16">mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>Delete All</span>
          </v-tooltip>
        </v-list-subheader>
      </v-list-item>
      <v-divider />
      <v-list-item
        v-for="(notification, i) in filteredNotifications"
        :key="i"
        v-model="selectedNotification"
        class="py-0"
        :value="notification">
        <div
          :class="{
            'pendo-dc-open-shared-from-notification': isFileSharedNotification(
              notification,
            ),
          }"
          @click="showNotification(notification)">
          <v-list-item-title class="d-flex">
            <span class="text-subtitle-2">{{ notification.subject }}</span>
            <v-spacer />
            <span class="text-caption">
              {{ $filters.fromNow(notification.created_at) }}
            </span>
          </v-list-item-title>
          <v-list-item-subtitle class="text-caption pl-2">
            <span class="notification-msg">
              {{ notification.message }}
            </span>
          </v-list-item-subtitle>
        </div>
        <template #append>
          <v-col class="d-flex flex-column pr-0">
            <v-tooltip location="right">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  class="icon-button"
                  variant="text"
                  size="x-small"
                  icon
                  @click="toggleRead(notification)">
                  <v-icon v-if="!notification.read" color="accent">
                    mdi-circle
                  </v-icon>
                  <v-icon v-else>mdi-circle-outline</v-icon>
                </v-btn>
              </template>
              <span v-if="notification.read">Mark Unread</span>
              <span v-else>Mark Read</span>
            </v-tooltip>
            <v-tooltip
              v-if="
                notification.meta_data?.route &&
                notification.meta_data?.route !== 'current_page'
              "
              location="right">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  class="icon-button"
                  variant="text"
                  size="x-small"
                  icon
                  @click="copyLink(notification)">
                  <v-icon>mdi-link-variant</v-icon>
                </v-btn>
              </template>
              <span>Copy link</span>
            </v-tooltip>
            <v-tooltip location="right">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  class="icon-button"
                  variant="text"
                  size="x-small"
                  icon
                  @click="remove(notification)">
                  <v-icon color="error">mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Delete</span>
            </v-tooltip>
          </v-col>
        </template>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import { isFileSharedNotification } from "@/helpers/pendoDownloadCenter";
import { userTierMixin } from "@/mixins/UserTier";
import { mapStores } from "pinia";
import { useUserStore } from "@/stores/UserStore";
import { useNotificationStore } from "@/stores/NotificationStore";

export default {
  name: "Notifications",
  mixins: [userTierMixin],
  data: () => ({
    menuOpen: false,
    selectedNotification: undefined,
  }),
  computed: {
    ...mapStores(useUserStore, useNotificationStore),
    filteredNotifications() {
      return this.notificationStore.notifications.filter(
        (n) => n.subject !== "header_alert",
      );
    },
    unreadNotifications() {
      return this.filteredNotifications.filter((n) => !n.read);
    },
    hasUnreadNotifications() {
      return this.unreadNotifications?.length > 0;
    },
  },
  watch: {
    loggedIn() {
      setTimeout(this.initNotifications);
    },
    unreadNotifications(newValue, oldValue) {
      if (newValue.length > 0 && newValue.length > oldValue.length) {
        const badge = document.querySelector(".v-badge--dot");

        if (!badge.classList.contains("bounce")) {
          badge.classList.add("bounce");
          setTimeout(() => {
            badge.classList.remove("bounce");
          }, 500);
        }
      }
    },
  },
  mounted() {
    this.initNotifications();
  },
  methods: {
    isFileSharedNotification,
    initNotifications() {
      if (this.loggedIn) {
        if (this.$ws) {
          this.$ws.subscribe({ channel: "NotificationChannel" });
        }
        this.notificationStore.index();
      }
    },
    showNotification(notification) {
      if (notification.meta_data?.route) {
        if (notification.meta_data.route === "current_page") {
          const { dashboard_tab, audience_name, org_id, output_rate_card_id } =
            notification.meta_data;
          if (dashboard_tab === "rate_card") {
            this.$router
              .push({
                name: "tv_ratings",
                query: {
                  rate_card_id: output_rate_card_id.toString(),
                  rate_card_name: notification.message,
                },
              })
              .catch(() => {});
          } else {
            this.$router.push({
              name: "manage_audiences",
              query: {
                audience_name,
                ...(org_id && {
                  org_id,
                }),
              },
            });
          }
        } else {
          this.$router
            .push({
              name: notification.meta_data.route,
              query: notification.meta_data.params,
            })
            .catch(() => {});
        }
        this.menuOpen = false;
      }

      if (!notification.read) {
        this.toggleRead(notification);
      }
    },
    toggleRead(notification) {
      notification.read = !notification.read;
      this.notificationStore.updateNotification(notification);
    },
    copyLink(notification) {
      const route = this.$router.resolve({
        name: notification.meta_data.route,
        query: notification.meta_data.params,
      });
      navigator.clipboard.writeText(`${window.location.origin}/${route.href}`);
    },
    remove(notification) {
      this.notificationStore.removeNotification(notification);
      if (this.filteredNotifications.length === 0) {
        this.menuOpen = false;
      }
    },
    removeAll() {
      this.notificationStore.removeAll();
      this.menuOpen = false;
    },
  },
};
</script>

<style scoped lang="scss">
@keyframes bounce-in {
  0% {
    transform: scale(0);
  }

  50% {
    transform: scale(1.5);
  }

  100% {
    transform: scale(1);
  }
}

.bounce {
  animation: bounce-in 0.5s;
}

.notification-msg {
  white-space: pre-line;
}

.text-subtitle-2 {
  font-family: Barlow-Bold, sans-serif;
  font-size: 16px !important;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  padding: 0;
  min-height: 48px;

  .v-list-subheader {
    width: 100%;
    padding: 0 !important;

    :deep(.v-list-subheader__text) {
      font-family: Barlow-Bold, sans-serif;
      width: 100% !important;
      justify-content: space-between;
      display: flex;
    }
  }

  :deep(.v-list-item__content) {
    width: 100% !important;
  }
}
</style>
